/**
 * Anagrafica Form Manager Component
 * Allows downloading blank forms and importing completed ones
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Download, Upload, FileText, FileSpreadsheet, 
  CheckCircle, XCircle, AlertTriangle, Loader2,
  Users, Building2, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnagraficaFormManager({ condominiumId, condominiumName }) {
  const { token } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [unitsCount, setUnitsCount] = useState('1');
  const [rowsCount, setRowsCount] = useState('20');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  // Download PDF form
  const handleDownloadPdf = useCallback(async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/condominiums/${condominiumId}/anagrafica-form/pdf?units=${unitsCount}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Download fallito');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Modulo_Anagrafica_${condominiumName?.replace(/\s+/g, '_') || 'Condominio'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Modulo PDF scaricato');
    } catch (error) {
      toast.error('Errore download: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  }, [condominiumId, condominiumName, token, unitsCount]);

  // Download Excel template
  const handleDownloadExcel = useCallback(async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/condominiums/${condominiumId}/anagrafica-form/excel?rows=${rowsCount}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Download fallito');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Template_Anagrafica_${condominiumName?.replace(/\s+/g, '_') || 'Condominio'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template Excel scaricato');
    } catch (error) {
      toast.error('Errore download: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  }, [condominiumId, condominiumName, token, rowsCount]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/pdf'
    ];
    const validExtensions = ['.xlsx', '.xls', '.pdf'];
    
    const hasValidExtension = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );
    
    if (!validTypes.includes(file.type) && !hasValidExtension) {
      toast.error('Formato non supportato. Usa Excel (.xlsx, .xls) o PDF.');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await fetch(
        `${API_URL}/api/condominiums/${condominiumId}/anagrafica-form/import`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      const result = await response.json();
      setImportResult(result);
      
      if (result.success) {
        toast.success(result.message || 'Importazione completata');
      } else {
        toast.error(result.message || 'Importazione fallita');
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: error.message,
        errors: [error.message]
      });
      toast.error('Errore importazione: ' + error.message);
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  }, [condominiumId, token]);

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <ClipboardList className="h-4 w-4" />
          Genera Modulo Anagrafica
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Modulo Anagrafica Condomini
          </DialogTitle>
          <DialogDescription>
            Scarica il modulo vuoto da far compilare ai condomini, oppure importa un modulo compilato
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Download Section */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Download className="h-4 w-4" />
              Scarica Modulo Vuoto
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              {/* PDF Download */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4 text-red-500" />
                    Modulo PDF
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Da stampare e far compilare a mano
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">N° unità da includere</Label>
                    <Select value={unitsCount} onValueChange={setUnitsCount}>
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 unità</SelectItem>
                        <SelectItem value="2">2 unità</SelectItem>
                        <SelectItem value="3">3 unità</SelectItem>
                        <SelectItem value="5">5 unità</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleDownloadPdf} 
                    disabled={isDownloading}
                    className="w-full"
                    size="sm"
                  >
                    {isDownloading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Scarica PDF
                  </Button>
                </CardContent>
              </Card>
              
              {/* Excel Download */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-green-500" />
                    Template Excel
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Per importazione multipla
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">N° righe vuote</Label>
                    <Select value={rowsCount} onValueChange={setRowsCount}>
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 righe</SelectItem>
                        <SelectItem value="20">20 righe</SelectItem>
                        <SelectItem value="50">50 righe</SelectItem>
                        <SelectItem value="100">100 righe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleDownloadExcel} 
                    disabled={isDownloading}
                    className="w-full"
                    size="sm"
                    variant="outline"
                  >
                    {isDownloading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Scarica Excel
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <Separator />
          
          {/* Upload Section */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Importa Modulo Compilato
            </h3>
            
            <Card className="border-dashed">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Upload className="h-6 w-6 text-blue-600" />
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium">Carica file compilato</p>
                    <p className="text-xs text-muted-foreground">
                      Excel (.xlsx, .xls) o PDF scansionato
                    </p>
                  </div>
                  
                  <div>
                    <Input
                      type="file"
                      accept=".xlsx,.xls,.pdf"
                      onChange={handleFileUpload}
                      disabled={isUploading}
                      className="max-w-xs mx-auto"
                    />
                  </div>
                  
                  {isUploading && (
                    <div className="space-y-2">
                      <Progress value={uploadProgress} className="h-2" />
                      <p className="text-xs text-muted-foreground">
                        Elaborazione in corso...
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Import Result */}
            {importResult && (
              <Alert variant={importResult.success ? "default" : "destructive"}>
                {importResult.success ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium">{importResult.message}</p>
                    
                    {importResult.success && (
                      <div className="flex gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Badge variant="default">{importResult.created || 0}</Badge>
                          nuovi
                        </span>
                        <span className="flex items-center gap-1">
                          <Badge variant="secondary">{importResult.updated || 0}</Badge>
                          aggiornati
                        </span>
                      </div>
                    )}
                    
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium mb-1">Errori:</p>
                        <ul className="text-xs list-disc list-inside">
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {importResult.errors.length > 5 && (
                            <li>...e altri {importResult.errors.length - 5} errori</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          {/* Info */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Suggerimento:</strong> Per importazioni multiple, usa il template Excel.
              Il PDF è consigliato per singoli condomini che compilano a mano.
              I dati vengono automaticamente riconosciuti e inseriti nel database.
            </AlertDescription>
          </Alert>
        </div>
      </DialogContent>
    </Dialog>
  );
}
